import { handle } from 'redux-pack';

import { get, patch, post, del, put } from 'lib/communication';
import { append, assocPath, groupBy, prop, path } from 'ramda';
import createReducer from 'lib/createReducer';
import { store } from '../../main';

const DOCUMENT_API_ROOT = '/documents';

// Static string to give as deletion reason when discarding a doc with no finished commits
const STATIC_DELETION_REASON = 'Document creation cancelled.';

/* Action types */

const GET_BY_ID = 'mystellarq/documents/GET_BY_ID';
const GET_ALL_BY_TYPE = 'mystellarq/documents/GET_ALL_BY_TYPE';
const GET_ALL = 'mystellarq/documents/GET_ALL';
const CREATE = 'mystellarq/documents/CREATE';
const DELETE = 'mystellarq/documents/DELETE';
const CREATE_COMMIT = 'mystellarq/documents/CREATE_COMMIT';
const UPDATE_COMMIT = 'mystellarq/documents/UPDATE_COMMIT';
const FINISH_COMMIT = 'mystellarq/documents/FINISH_COMMIT';
const DISCARD_COMMIT = 'mystellarq/documents/DISCARD_COMMIT';
const COMMIT_PUT = 'mystellarq/documents/COMMIT_PUT';

// Helper / combination types
const CREATE_DOCUMENT_AND_COMMIT = 'mystellarq/documents/CREATE_DOCUMENT_AND_COMMIT';

export const actionTypes = {
  GET_BY_ID,
  GET_ALL_BY_TYPE,
  GET_ALL,
  CREATE,
  DELETE,
  CREATE_COMMIT,
  UPDATE_COMMIT,
  FINISH_COMMIT,
  DISCARD_COMMIT,
  COMMIT_PUT,
  CREATE_DOCUMENT_AND_COMMIT,
};

/* Util */

/**
 * Create client side placeholder commit.
 *
 * Purpose of this is to create a commit data structure that resembles what is stored in backend.
 * We need this because creates do not return the actual created entity from backend. This could be fixed there.
 *
 * @param {*} id Real commit Id
 * @param {*} dateCommitted Commit time, null for unfinished.
 * @param {*} data Commit's data
 * @returns {ICommit} A commit object
 */
const createPlaceholderCommit = (id, dateCommitted = null, data = {}) => ({
  commitId: id,
  commitDate: dateCommitted,
  data: data || {},
});

/**
 * Convert a create API response to placeholder client side data structure
 *
 * See createPlaceholderCommit for reasoning
 *
 * @param {*} docType Document type
 * @param {*} docId Real document Id
 * @param {*} commitId Real commit Id (if present)
 * @param {*} commitData Real commit data (if present)
 * @returns {IDocument} Document data object
 */
const responseToPlaceholder = (docType, docId, commitId, commitData) => {
  if (!docType || !docId) {
    throw new Error('Placeholder document requires document type and document ID.');
  }

  if (commitData && !commitId) {
    throw new Error('Placeholder document requires data to be provided with commitId');
  }

  const placeholderDocument = {
    documentId: docId,
    documentType: docType,
    createDate: Date.now(),
    commits: [],
  };

  if (commitId) {
    placeholderDocument.commits = [createPlaceholderCommit(commitId, null, commitData)];
  }

  return placeholderDocument;
};

/* Action creators */

/**
 * Get a document by type and ID
 * @param {string} type Document type
 * @param {string} id Document ID
 * @returns {{ type: string, promise: Promise<Document> }} Dispatchable redux action
 */
const getDocumentById = (docType, id) => ({
  type: GET_BY_ID,
  meta: { docType, id },
  promise: get(`${DOCUMENT_API_ROOT}/${docType}/${id}`),
});

/**
 * Get all documents by type
 * @param {string} docType Document type
 * @returns {{ type: string, promise: Promise<Array<Document>> }} Dispatchable redux action
 */
const getAllDocumentsByType = docType => ({
  type: GET_ALL_BY_TYPE,
  meta: { docType },
  promise: get(`${DOCUMENT_API_ROOT}/${docType}`),
});

/**
 * Get all documents
 * @returns {{ type: string, promise: Promise<Array<Document>> }} Dispatchable redux action
 */
const getAllDocuments = () => ({
  type: GET_ALL,
  promise: get(DOCUMENT_API_ROOT),
});

/**
 * Creates new document
 * @param {string} docType Document type
 * @returns {{ type: string, promise: Promise<{ documentId: string }> }} Dispatchable redux action
 */
const createDocument = docType => ({
  type: CREATE,
  meta: { docType },
  promise: post(`${DOCUMENT_API_ROOT}/${docType}`),
});

/**
 * Create new commit
 * @param {string} docType Document type
 * @param {string} documentId Document ID
 * @returns {{ type: string, promise: Promise<{ commitId: string }> }} Dispatchable redux action
 */
const createCommit = (docType, documentId, initialData = null) => ({
  type: CREATE_COMMIT,
  meta: { docType, documentId, initialData },
  promise: post(`${DOCUMENT_API_ROOT}/${docType}/${documentId}`).then(({ commitId }) => {
    if (initialData) {
      return patch(`${DOCUMENT_API_ROOT}/${docType}/${documentId}/${commitId}`, initialData)
        .then(() => {
          return { commitId };
        })
        .catch(err => {
          console.error('Could not update data on created commit');
          return Promise.reject(err);
        });
    } else {
      return { commitId };
    }
  }),
});

/**
 * Discard an existing commit.
 * If the discard event would result in an empty document, this will fire deleteDocument instead.
 * @param {string} docType Document type
 * @param {string} documentId Document ID
 * @param {string} commitId Commit ID
 * @returns {{ type: string, promise: Promise<void> } | { type: 'NOOP' }} Dispatchable redux action
 */
const discardCommit = (docType, documentId, commitId) => {
  try {
    const moduleState = selectModuleState(docType, store.getState().newDocs);
    const document = moduleState.documents.find(d => d.documentId === documentId);
    // If only single commit exists and it would be discarded, fire document deletion action instead
    if (document.commits.length === 1 && document.commits[0].commitId === commitId) {
      return deleteDocument(docType, documentId, { reason: { reason: STATIC_DELETION_REASON }, date: Date.now() });
    }
    return {
      type: DISCARD_COMMIT,
      meta: { docType, documentId, commitId },
      promise: del(`${DOCUMENT_API_ROOT}/${docType}/${documentId}/${commitId}`),
    };
  } catch (e) {
    console.error('Commit discard action failed. This may be cause by uninitialized state or missing arguments.');
    console.error(e);
    return { type: 'NOOP' }; // Dispatch an empty action to not crash redux here
  }
};

/**
 * Finish an existing commit
 * @param {string} docType Document type
 * @param {string} documentId Document ID
 * @param {string} commitId Commit ID
 * @param {{ [key: string]: any }} data Commit content
 * @returns {{ type: string, promise: Promise<void> }} Dispatchable redux action
 */
const finishCommit = (docType, documentId, commitId, data) => ({
  type: FINISH_COMMIT,
  meta: { docType, documentId, commitId, data },
  promise: post(`${DOCUMENT_API_ROOT}/${docType}/${documentId}/${commitId}`, data),
});

/**
 * Update an existing commit
 * @param {string} docType Document type
 * @param {string} documentId Document ID
 * @param {string} commitId Commit ID
 * @param {{ [key: string]: any }} data Commit content
 * @returns {{ type: string, promise: Promise<void> }} Dispatchable redux action
 */
const updateCommit = (docType, documentId, commitId, data) => ({
  type: UPDATE_COMMIT,
  meta: { docType, documentId, commitId, data },
  promise: patch(`${DOCUMENT_API_ROOT}/${docType}/${documentId}/${commitId}`, data),
});

/**
 * Mark an existing document as deleted.
 *
 * @param {string} docType documentType
 * @param {string} documentId documentId, UUID
 * @param {{ reason: any; date: number }} reason
 */
const deleteDocument = (docType, documentId, reason) => ({
  type: DELETE,
  meta: { docType, documentId },
  promise: del(`${DOCUMENT_API_ROOT}/${docType}/${documentId}`, { body: JSON.stringify(reason) }),
});

/**
 * Create a document and commit with optional initial data
 * @param {*} docType
 * @param {*} initialData
 * @returns
 */
const createDocumentAndCommit = (docType, initialData = null) => ({
  type: CREATE_DOCUMENT_AND_COMMIT,
  meta: { initialData, docType },
  promise: post(`${DOCUMENT_API_ROOT}/${docType}`)
    .then(createResponse => {
      const docId = createResponse.documentId;

      return post(`${DOCUMENT_API_ROOT}/${docType}/${docId}`).then(commitResponse => {
        const commitId = commitResponse.commitId;

        if (initialData) {
          return patch(`${DOCUMENT_API_ROOT}/${docType}/${docId}/${commitId}`, initialData)
            .then(() => {
              return { docId, commitId, data: initialData };
            })
            .catch(() => {
              console.error(`Failed to set initial data on document ${docId} commit ${commitId}.`);
              return Promise.resolve({ docId, commitId, data: null });
            });
        } else {
          return { docId, commitId, data: null };
        }
      });
    })
    .catch(err => {
      console.error(`Failed to create document with type ${docType}`);
      return Promise.reject(err);
    }),
});

const commitPut = (docType, documentId, data) => ({
  type: COMMIT_PUT,
  meta: { docType, documentId, data },
  promise: put(`${DOCUMENT_API_ROOT}/${docType}/${documentId}`, data)
    .then(response => {
      return { commitId: response.commitId, docType, documentId, data };
    })
    .catch(e => {
      console.error(`Commit PUT request failed on document ` + documentId);
      return Promise.reject(e);
    }),
});

export const actions = {
  getDocumentById,
  getAllDocumentsByType,
  getAllDocuments,
  createDocument,
  createCommit,
  discardCommit,
  finishCommit,
  updateCommit,
  createDocumentAndCommit,
  deleteDocument,
  commitPut,
};

export const MODULE_ACCESSOR = 'modules';
export const IS_LOADING_ALL_KEY = 'isLoadingAll';
export const IS_LOADING_KEY = 'isLoading';
export const ERROR_KEY = 'requestError';

const assocModuleKey = (doc, key, value) => assocPath([MODULE_ACCESSOR, doc, key], value);

const commonPromiseHandlers = action => ({
  start: assocModuleKey(action.meta.docType, IS_LOADING_KEY, true),
  finish: assocModuleKey(action.meta.docType, IS_LOADING_KEY, false),
  failure: prevState => assocPath(ERROR_KEY, action.payload, prevState),
});

const initialState = {
  [MODULE_ACCESSOR]: {},
  [IS_LOADING_ALL_KEY]: false,
  [ERROR_KEY]: null,
};

const initialModuleState = {
  documents: [],
  [IS_LOADING_KEY]: false,
  [ERROR_KEY]: null,
};

/**
 * Map documents by document type from an array to a key-value pair
 * @param {IDocument[]} docs Array of documents
 * @returns {{ [key: string]: IDocument[] }} Map of documents
 */
const mapDocumentsByType = docs => {
  const groups = groupBy(prop('documentType'), docs || []);
  let mapped = {};
  for (const [docType, documents] of Object.entries(groups)) {
    // the backend should not return removed documents?
    mapped[docType] = { ...initialModuleState, documents: documents.filter(d => !Number.isInteger(d.removeTS)) };
  }
  return mapped;
};

const handleLoadAll = (s, a) =>
  handle(s, a, {
    start: assocPath([IS_LOADING_ALL_KEY], true),
    finish: assocPath([IS_LOADING_ALL_KEY], false),
    failure: preState => ({ ...preState, [ERROR_KEY]: a.payload }),
    success: preState => ({ ...preState, [MODULE_ACCESSOR]: mapDocumentsByType(a.payload) }),
  });

const handleLoadAllByType = (s, a) =>
  handle(s, a, {
    ...commonPromiseHandlers(a),
    success: preState => {
      return assocPath([MODULE_ACCESSOR, a.meta.docType], { ...initialModuleState, documents: a.payload }, preState);
    },
  });

const handleDocumentAndCommitCreate = (s, a) =>
  handle(s, a, {
    ...commonPromiseHandlers(a),
    success: preState => {
      const clientSideDocument = responseToPlaceholder(
        a.meta.docType,
        a.payload.docId,
        a.payload.commitId,
        a.payload.data,
      );
      const prevDocuments = path([MODULE_ACCESSOR, a.meta.docType, 'documents'], preState) || [];
      return assocModuleKey(a.meta.docType, 'documents', append(clientSideDocument, prevDocuments))(preState);
    },
  });

const handleCommitCreate = (s, a) =>
  handle(s, a, {
    ...commonPromiseHandlers(a),
    success: preState => {
      const documents = selectModuleState(a.meta.docType, preState).documents;
      const docIndex = documents.findIndex(d => d.documentId === a.meta.documentId);
      const clientSideCommit = createPlaceholderCommit(a.payload.commitId, null, a.meta.initialData);
      return assocPath(
        [MODULE_ACCESSOR, a.meta.docType, 'documents', docIndex, 'commits'],
        append(clientSideCommit, documents[docIndex].commits),
        preState,
      );
    },
  });

const handleCommitUpdate = (s, a) =>
  handle(s, a, {
    ...commonPromiseHandlers(a),
    success: preState => {
      const documents = selectModuleState(a.meta.docType, preState)['documents'];
      const docIndex = documents.findIndex(d => d.documentId === a.meta.documentId);
      const commitIndex = documents[docIndex].commits.findIndex(c => c.commitId === a.meta.commitId);
      return assocPath(
        [MODULE_ACCESSOR, a.meta.docType, 'documents', docIndex, 'commits', commitIndex, 'data'],
        a.meta.data,
        preState,
      );
    },
  });

const handleCommitDiscard = (s, a) =>
  handle(s, a, {
    ...commonPromiseHandlers(a),
    success: preState => {
      const documents = selectModuleState(a.meta.docType, preState)['documents'];
      const docIndex = documents.findIndex(d => d.documentId === a.meta.documentId);
      const newCommits = documents[docIndex].commits.filter(c => c.commitId !== a.meta.commitId);
      return assocPath([MODULE_ACCESSOR, a.meta.docType, 'documents', docIndex, 'commits'], newCommits, preState);
    },
  });

const handleCommitFinish = (s, a) =>
  handle(s, a, {
    ...commonPromiseHandlers(a),
    success: preState => {
      const documents = selectModuleState(a.meta.docType, preState)['documents'];
      const docIndex = documents.findIndex(d => d.documentId === a.meta.documentId);
      const commitIndex = documents[docIndex].commits.findIndex(c => c.commitId === a.meta.commitId);
      return assocPath(
        [MODULE_ACCESSOR, a.meta.docType, 'documents', docIndex, 'commits', commitIndex, 'commitDate'],
        Date.now(),
        preState,
      );
    },
  });

const handleDocumentDelete = (s, a) =>
  handle(s, a, {
    ...commonPromiseHandlers(a),
    success: preState => {
      const docsAfter = selectModuleState(a.meta.docType, preState)['documents'].filter(
        d => d.documentId !== a.meta.documentId,
      );
      return assocPath([MODULE_ACCESSOR, a.meta.docType, 'documents'], docsAfter, preState);
    },
  });

const handleCommitPut = (s, a) =>
  handle(s, a, {
    ...commonPromiseHandlers(a),
    success: preState => {
      const documents = selectModuleState(a.meta.docType, preState).documents;
      const docIndex = documents.findIndex(d => d.documentId === a.meta.documentId);
      const clientSideCommit = createPlaceholderCommit(a.payload.commitId, Date.now(), a.meta.data);
      return assocPath(
        [MODULE_ACCESSOR, a.meta.docType, 'documents', docIndex, 'commits'],
        append(clientSideCommit, documents[docIndex].commits),
        preState,
      );
    },
  });

const HANDLERS = {
  [GET_ALL]: handleLoadAll,
  [GET_ALL_BY_TYPE]: handleLoadAllByType,
  [CREATE_DOCUMENT_AND_COMMIT]: handleDocumentAndCommitCreate,
  [CREATE_COMMIT]: handleCommitCreate,
  [DISCARD_COMMIT]: handleCommitDiscard,
  [FINISH_COMMIT]: handleCommitFinish,
  [UPDATE_COMMIT]: handleCommitUpdate,
  [DELETE]: handleDocumentDelete,
  [COMMIT_PUT]: handleCommitPut,
};

export default createReducer(initialState, HANDLERS);

const selectModuleState = (module, state) => {
  if (!state || MODULE_ACCESSOR in state === false) {
    throw new Error('Passed state does not seem to contain module key:' + MODULE_ACCESSOR);
  }

  if (!module) throw new Error('Module not specified.');
  if (module in state[MODULE_ACCESSOR] === false) return null;

  if (__ROLE__ === 'DOCTOR') {
    return {
      ...state[MODULE_ACCESSOR][module],
      documents:
        state[MODULE_ACCESSOR][module].documents &&
        state[MODULE_ACCESSOR][module].documents
          .map(d => ({ ...d, commits: d.commits.filter(c => c.commitDate) }))
          .filter(d => d.commits && d.commits.length !== 0),
    };
  }
  return state[MODULE_ACCESSOR][module];
};

const selectAvailableModules = state => {
  if (!state || MODULE_ACCESSOR in state === false) {
    throw new Error('Passed state does not seem to contain module key:' + MODULE_ACCESSOR);
  }
  return Object.keys(state[MODULE_ACCESSOR]);
};

export const selectors = {
  selectModuleState,
  selectAvailableModules,
};
